/** Flatten sibling import rows into AG Grid tree-data rows (project → alias / draft). */

import { classNameFromAlias, defaultClassName, generatedClassFullName } from './siblingAliasDisplay.js';

/** "My APIs" → "MyAPIs"; names that start with a digit get a leading _. */
export function sanitizeImportAlias(name) {
    let s = String(name || '').replace(/[^A-Za-z0-9_]+/g, '');
    if (!s)
        return 'Alias';
    if (/^[0-9]/.test(s))
        s = `_${s}`;
    return s;
}

export function uniqueImportAlias(seed, isTaken) {
    const base = sanitizeImportAlias(seed);
    if (typeof isTaken !== 'function' || !isTaken(base))
        return base;
    for (let i = 2; i < 100; i++) {
        const candidate = `${base}${i}`;
        if (!isTaken(candidate))
            return candidate;
    }
    return base;
}

export function defaultImportAlias(projectName, suggestAlias) {
    if (typeof suggestAlias === 'function') {
        const suggested = suggestAlias(projectName);
        if (suggested)
            return suggested;
    }
    return sanitizeImportAlias(projectName);
}

/**
 * The last remaining alias on an import cannot be deleted (rename it, or
 * remove a manual import). Extra aliases stay deletable. Canvas default-class
 * aliases stay even when extras exist.
 */
export function isProtectedImportAlias(row, alias) {
    if (!row || !alias)
        return false;
    if (alias._implied)
        return true;
    const aliases = (row.Aliases || []).filter((a) => a?.Alias && String(a.Alias).trim());
    if (aliases.length <= 1)
        return true;
    if (row.Source === 'Canvas') {
        const className = classNameFromAlias(row, alias);
        const def = defaultClassName(row.Name);
        return !className || className === def;
    }
    return false;
}

export function buildImportTreeRows(rows, drafts, options = {}) {
    const out = [];
    const suggestAlias = options.suggestAlias;
    for (const row of rows || []) {
        const id = row.ProjectId;
        if (!id)
            continue;
        const aliases = (row.Aliases || []).filter((a) => a?.Alias && String(a.Alias).trim());
        const draft = drafts?.[id];
        const impliedName = !aliases.length
            ? defaultImportAlias(row.Name, suggestAlias)
            : '';
        const showDraft = !!draft;
        out.push({
            ID: id,
            rowKind: 'project',
            Name: row.Name || '',
            project: row,
        });
        for (const alias of aliases) {
            out.push({
                ID: `${id}::${alias.Alias}`,
                rowKind: 'alias',
                Name: alias.Alias,
                projectId: id,
                project: row,
                alias,
            });
        }
        if (impliedName) {
            const className = defaultClassName(row.Name);
            out.push({
                ID: `${id}::implied`,
                rowKind: 'alias',
                Name: impliedName,
                projectId: id,
                project: row,
                implied: true,
                alias: {
                    Alias: impliedName,
                    ClassFullName: generatedClassFullName(row.Name, className),
                    _implied: true,
                },
            });
        }
        if (showDraft) {
            out.push({
                ID: `${id}::draft`,
                rowKind: 'draft',
                Name: '',
                projectId: id,
                project: row,
            });
        }
    }
    return out;
}
